"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _systemConstants = require("@telus-uds/system-constants");
var _utils = require("../utils");
var _Row = _interopRequireDefault(require("./Row"));
var _Col = _interopRequireDefault(require("./Col"));
var _GutterContext = _interopRequireDefault(require("./providers/GutterContext"));
var _helpers = _interopRequireDefault(require("./helpers"));
var _ThemeProvider = require("../ThemeProvider");
var _ViewportProvider = require("../ViewportProvider");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const [selectProps, selectedSystemPropTypes] = (0, _utils.selectSystemProps)([_utils.a11yProps, _utils.viewProps]);

/**
 * A mobile-first flexbox grid.
 */

const FlexGrid = /*#__PURE__*/_react.default.forwardRef((_ref, ref) => {
  let {
    limitWidth = true,
    gutter = true,
    outsideGutter = true,
    xsReverse,
    smReverse,
    mdReverse,
    lgReverse,
    xlReverse,
    tag,
    accessibilityRole,
    children,
    dataSet,
    ...rest
  } = _ref;
  const reverseLevel = (0, _helpers.default)([xsReverse, smReverse, mdReverse, lgReverse, xlReverse]);
  const viewport = (0, _ViewportProvider.useViewport)();
  const {
    themeOptions: {
      enableMediaQueryStyleSheet
    }
  } = (0, _ThemeProvider.useTheme)();
  let flexgridStyles;
  let mediaIds;
  const stylesByViewport = {
    xs: {
      flexDirection: reverseLevel[0] ? 'column-reverse' : 'column'
    },
    sm: {
      maxWidth: limitWidth && _systemConstants.viewports.map.get('sm'),
      flexDirection: reverseLevel[1] ? 'column-reverse' : 'column'
    },
    md: {
      maxWidth: limitWidth && _systemConstants.viewports.map.get('md'),
      flexDirection: reverseLevel[2] ? 'column-reverse' : 'column'
    },
    lg: {
      maxWidth: limitWidth && _systemConstants.viewports.map.get('lg'),
      flexDirection: reverseLevel[3] ? 'column-reverse' : 'column'
    },
    xl: {
      maxWidth: limitWidth && _systemConstants.viewports.map.get('xl'),
      flexDirection: reverseLevel[4] ? 'column-reverse' : 'column'
    }
  };
  const staticStyles = {
    flexWrap: 'wrap',
    width: outsideGutter ? '100%' : 'auto',
    marginVertical: 0,
    marginHorizontal: outsideGutter ? 'auto' : -16
  };
  if (enableMediaQueryStyleSheet) {
    const mediaQueryStyles = (0, _utils.createMediaQueryStyles)(stylesByViewport);
    const {
      ids,
      styles
    } = _utils.StyleSheet.create({
      flexgrid: {
        ...staticStyles,
        ...mediaQueryStyles
      }
    });
    flexgridStyles = styles.flexgrid;
    mediaIds = ids.flexgrid;
  } else {
    flexgridStyles = {
      ...staticStyles,
      ...stylesByViewport[viewport]
    };
  }
  const props = {
    accessibilityRole,
    ...(0, _utils.getA11yPropsFromHtmlTag)(tag, accessibilityRole),
    ...selectProps(rest)
  };
  const dataSetValue = mediaIds ? {
    media: mediaIds,
    ...dataSet
  } : dataSet;
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_GutterContext.default.Provider, {
    value: gutter,
    children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_utils.BaseView, {
      ref: ref,
      ...props,
      style: flexgridStyles,
      dataSet: dataSetValue,
      children: children
    })
  });
});
FlexGrid.displayName = 'FlexGrid';
FlexGrid.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * Whether or not to give the grid a fixed width. This also centres the grid horizontally.
   */
  limitWidth: _propTypes.default.bool,
  /**
   * Whether or not to include gutters in between columns.
   */
  gutter: _propTypes.default.bool,
  /**
   * Whether or not to include gutter at the ends to the left and right of the FlexGrid
   */
  outsideGutter: _propTypes.default.bool,
  /**
   * Choose if the item order should be reversed from the 'xs' breakpoint. When you pass in false, the order will be normal from the xs breakpoint. By default, it inherits the behaviour set by the preceding prop.
   */
  xsReverse: _propTypes.default.bool,
  /**
   * Choose if the item order should be reversed from the 'sm' breakpoint. When you pass in false, the order will be normal from the sm breakpoint. By default, it inherits the behaviour set by the preceding prop.
   */
  smReverse: _propTypes.default.bool,
  /**
   * Choose if the item order should be reversed from the 'md' breakpoint. When you pass in false, the order will be normal from the md breakpoint. By default, it inherits the behaviour set by the preceding prop.
   */
  mdReverse: _propTypes.default.bool,
  /**
   * Choose if the item order should be reversed from the 'lg' breakpoint. When you pass in false, the order will be normal from the lg breakpoint. By default, it inherits the behaviour set by the preceding prop.
   */
  lgReverse: _propTypes.default.bool,
  /**
   * Choose if the item order should be reversed from the 'xl' breakpoint. When you pass in false, the order will be normal from the xl breakpoint. By default, it inherits the behaviour set by the preceding prop.
   */
  xlReverse: _propTypes.default.bool,
  /**
   * Optional semantic HTML tag, to apply to the container on web. Does not change styling.
   *
   * In native apps, if a header tag is provided ("h1", "h2" etc) and accessibilityRole prop
   * is not defined, the accessibilityRole will default to "heading".
   */
  tag: _propTypes.default.oneOf(_utils.layoutTags),
  /**
   * The rows and columns of the Grid. Will typically be `FlexGrid.Row` and `FlexGrid.Col` components.
   */
  children: _propTypes.default.node.isRequired
};
FlexGrid.Row = _Row.default;
FlexGrid.Col = _Col.default;
var _default = exports.default = FlexGrid;